import { SwimlaneId } from "../constants";
import {
  ActivityFeedItemFragment,
  ActivityRelativeTypeEnum,
  ActivityTypeEnum,
  SwimlaneIdEnum,
} from "../schema";

const ALL_BOARDS: SwimlaneId[] = [
  SwimlaneId.PURCHASE_REQUEST,
  SwimlaneId.RFX,
  SwimlaneId.PURCHASE_ORDER,
  SwimlaneId.RECEIVING,
  SwimlaneId.INVOICE,
];

// SwimlaneIdEnum (schema) and SwimlaneId (constants) share the same string values
const SWIMLANE_TO_BOARD: Record<SwimlaneIdEnum, SwimlaneId> = {
  [SwimlaneIdEnum.PURCHASE_REQUEST]: SwimlaneId.PURCHASE_REQUEST,
  [SwimlaneIdEnum.RFX]: SwimlaneId.RFX,
  [SwimlaneIdEnum.PURCHASE_ORDER]: SwimlaneId.PURCHASE_ORDER,
  [SwimlaneIdEnum.RECEIVING]: SwimlaneId.RECEIVING,
  [SwimlaneIdEnum.INVOICE]: SwimlaneId.INVOICE,
};

/**
 * Given a live ActivityFeed event, return only the Kanban board column(s) that
 * could be affected, so the board can refetch those instead of all six queries
 * on every org-wide activity.
 *
 * Conservative by design: an unknown/undefined activity (e.g. a future activity
 * type) maps to ALL boards so a card update can never be silently missed.
 */
export function getActivityAffectedBoards(
  activity: ActivityFeedItemFragment | undefined,
): SwimlaneId[] {
  if (!activity) {
    return ALL_BOARDS;
  }

  // A drag/transition publishes a single activity (often relativeType ORDER even
  // when dragged from the PR/RFX column), so the source + destination columns are
  // only reliable from the DRAG_CARD payload itself.
  if (
    activity.type === ActivityTypeEnum.DRAG_CARD &&
    activity.data &&
    activity.data.__typename === "DRAG_CARD"
  ) {
    return unique([
      SWIMLANE_TO_BOARD[activity.data.fromType],
      SWIMLANE_TO_BOARD[activity.data.toType],
    ]);
  }

  switch (activity.relativeType) {
    case ActivityRelativeTypeEnum.PURCHASE_REQUEST:
    case ActivityRelativeTypeEnum.PURCHASE_REQUEST_ITEM:
    case ActivityRelativeTypeEnum.PURCHASE_REQUEST_PRODUCT:
    case ActivityRelativeTypeEnum.PURCHASE_REQUEST_SERVICE:
      return [SwimlaneId.PURCHASE_REQUEST];

    case ActivityRelativeTypeEnum.RFX:
      return [SwimlaneId.RFX];

    case ActivityRelativeTypeEnum.ORDER:
      // Order, Receiving and Invoice columns are all projections of the Order
      // entity; refine by activity type, otherwise refetch all three to be safe.
      switch (activity.type) {
        case ActivityTypeEnum.UPDATE_ORDER_RECEIVED_QUANTITIES:
          return [SwimlaneId.PURCHASE_ORDER, SwimlaneId.RECEIVING];
        case ActivityTypeEnum.CREATE_INVOICE_FROM_ORDER:
          return [SwimlaneId.PURCHASE_ORDER, SwimlaneId.INVOICE];
        default:
          return [
            SwimlaneId.PURCHASE_ORDER,
            SwimlaneId.RECEIVING,
            SwimlaneId.INVOICE,
          ];
      }

    case ActivityRelativeTypeEnum.SUPPLIER:
      // supplier activities don't touch any board column
      return [];

    default:
      return ALL_BOARDS;
  }
}

function unique(boards: SwimlaneId[]): SwimlaneId[] {
  return boards.filter((b, i) => boards.indexOf(b) === i);
}
